import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Check, Sparkles, Lock, Unlock, Crown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Premium() {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tier/features`).then(r => setTiers(r.data?.tiers || []));
    // Refresh user on mount so unlock-on-payment-detection kicks in immediately
    if (refreshUser) refreshUser?.();
  }, [refreshUser]);

  const currentTier = (user?.tier || 'free').toLowerCase();
  const isPremium = currentTier === 'pro' || currentTier === 'studio';

  const upgrade = async (planId) => {
    if (!isAuthenticated) { navigate('/register'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payments/checkout`,
        { plan_id: planId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="premium-page">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-4">
          <Link to={isAuthenticated ? '/dashboard' : '/'} className="text-zinc-400 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex-1" />
          {isAuthenticated && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-zinc-500 font-mono uppercase">Your tier</span>
              <span className={`px-2 py-0.5 rounded-full font-mono uppercase text-[10px]
                ${isPremium ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}
                data-testid="premium-current-tier"
              >
                {currentTier}
              </span>
            </div>
          )}
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 pt-10 pb-4">
        <h1 className="text-4xl sm:text-5xl font-black mb-2 flex items-center gap-3">
          <Crown className="w-8 h-8 text-amber-400" /> Premium Features
        </h1>
        <p className="text-sm text-zinc-400 max-w-2xl">
          Everything that unlocks the moment your payment goes through. No manual toggles — Stripe webhooks flip your tier live.
        </p>
      </section>

      <main className="max-w-[1400px] mx-auto px-6 pb-16 grid md:grid-cols-3 gap-4">
        {tiers.map(t => {
          const isCurrent = currentTier === t.id;
          const isLocked = !isCurrent && (t.id !== 'free' && !isPremium);
          const higher = t.id !== 'free' && (t.id === 'studio' && currentTier !== 'studio');
          return (
            <article
              key={t.id}
              className={`rounded-2xl border p-6 flex flex-col ${
                isCurrent ? 'border-amber-500/60 bg-amber-500/5' :
                t.id === 'studio' ? 'border-[#06B6D4]/40 bg-[#06B6D4]/5' : 'border-[#1E1E24] bg-[#0A0A0C]'
              }`}
              data-testid={`premium-tier-${t.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-bold">{t.label}</h3>
                {isCurrent && <span className="text-[10px] font-mono uppercase bg-amber-500 text-black px-2 py-0.5 rounded-full">Active</span>}
              </div>
              <div className="mb-4">
                <span className="text-3xl font-black">${t.monthly_usd}</span>
                <span className="text-xs text-zinc-500">/month</span>
              </div>
              <ul className="space-y-2 flex-1 mb-6">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-zinc-300">
                    {isCurrent || t.id === 'free' ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                    ) : isLocked ? (
                      <Lock className="w-3.5 h-3.5 text-zinc-600 mt-0.5 shrink-0" />
                    ) : (
                      <Unlock className="w-3.5 h-3.5 text-[#06B6D4] mt-0.5 shrink-0" />
                    )}
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {t.id === 'free' ? (
                <Button disabled className="bg-zinc-800 text-zinc-500">Free forever</Button>
              ) : isCurrent ? (
                <Button disabled className="bg-amber-500/20 text-amber-300 border border-amber-500/40" data-testid={`premium-current-${t.id}`}>
                  <Sparkles className="w-4 h-4 mr-1" /> You have this
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => upgrade(`${t.id}_monthly`)}
                    disabled={busy}
                    className="flex-1 bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
                    data-testid={`premium-upgrade-${t.id}-monthly`}
                  >
                    Go {t.label} · Monthly
                  </Button>
                  <Button
                    onClick={() => upgrade(`${t.id}_yearly`)}
                    disabled={busy}
                    variant="outline"
                    className="flex-1 border-[#1E1E24] hover:border-[#06B6D4]"
                    data-testid={`premium-upgrade-${t.id}-yearly`}
                  >
                    Yearly · save 20%
                  </Button>
                </div>
              )}
              {higher && !isPremium && (
                <p className="text-[10px] text-zinc-500 font-mono mt-2 text-center">Unlocks instantly on payment</p>
              )}
            </article>
          );
        })}
      </main>

      {isPremium && (
        <section className="max-w-[1400px] mx-auto px-6 pb-16">
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6" data-testid="premium-unlocked-summary">
            <div className="flex items-center gap-2 mb-2">
              <Unlock className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-bold">Your unlocked features</h3>
            </div>
            <p className="text-xs text-zinc-400 mb-3">These are live on your account right now:</p>
            <div className="grid md:grid-cols-2 gap-2">
              {['No watermark', 'HD/4K exports', 'Private E2E DMs', 'Godot .tscn export', '100+ generations/day', 'Marketplace revenue share'].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-zinc-200">
                  <Check className="w-3.5 h-3.5 text-emerald-400" /> {f}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
